import { useCallback, useEffect, useState } from 'react';
import { trackPageView } from './analytics';
import { Checkout } from './components/Checkout';
import { ComparePlayer } from './components/ComparePlayer';
import { Dropzone } from './components/Dropzone';
import { PageModal } from './components/PageModal';
import { useMastering } from './hooks/useMastering';
import { useSession } from './hooks/useSession';

const PHASE_LABELS: Record<string, string> = {
    uploading: 'Uploading your track…',
    mastering: 'Mastering with AI…',
    ready: 'Your master is ready',
};

export default function App() {
    const { config, loading, error: sessionError, retry } = useSession();
    const mastering = useMastering(config);
    const [page, setPage] = useState<string | null>(null);

    // Deep links like /#faq open the matching page, as in the legacy SPA.
    useEffect(() => {
        const applyHash = () => {
            const slug = window.location.hash.replace(/^#/, '');
            setPage(slug || null);
        };
        applyHash();
        // The initial view is counted by the counter itself, only the virtual
        // navigations of the SPA have to be reported.
        const onHashChange = () => {
            applyHash();
            trackPageView(window.location.pathname + window.location.hash);
        };
        window.addEventListener('hashchange', onHashChange);
        return () => window.removeEventListener('hashchange', onHashChange);
    }, []);

    const closePage = useCallback(() => {
        setPage(null);
        if (window.location.hash) {
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, []);

    const busy = mastering.phase === 'uploading' || mastering.phase === 'mastering';

    return (
        <div className="app">
            <div
                className={`topbar-progress${busy ? ' is-visible' : ''}`}
                style={{ width: `${mastering.totalProgress}%` }}
                aria-hidden="true"
            />

            <header className="site-header">
                <a className="brand" href="/">
                    <span className="brand__mark" aria-hidden="true" />
                    <span className="brand__name">
                        Five Dollar <strong>Mastering</strong>
                    </span>
                </a>
                <nav className="site-nav">
                    <a href="#faq">FAQ</a>
                    <a href="mailto:fivedollamastering@gmail.com?subject=Contacting%20from%20fivedollarmastering.com">
                        Contact
                    </a>
                </nav>
            </header>

            <main className="hero">
                <section className="hero__intro">
                    <p className="hero__eyebrow">AI powered audio mastering</p>
                    <h1 className="hero__title">
                        Your track, radio ready
                        <br />
                        in about a minute.
                    </h1>
                    <p className="hero__lead">
                        Upload your mix, listen to the mastered preview for free, and only pay when you
                        love how it sounds. No account, no subscription.
                    </p>
                    {config && (
                        <ul className="hero__facts">
                            <li>
                                <strong>
                                    {config.currency === 'USD' ? '$' : ''}
                                    {config.price}
                                </strong>
                                per track
                            </li>
                            <li>
                                <strong>{config.acceptedFileTypes.join(', ').toUpperCase()}</strong>
                                supported
                            </li>
                            <li>
                                <strong>24 bit</strong>WAV master
                            </li>
                        </ul>
                    )}
                </section>

                <section className="hero__panel">
                    {loading && <p className="panel-note">Warming up the studio…</p>}

                    {sessionError && (
                        <div className="alert alert--error">
                            <p>{sessionError}</p>
                            <button type="button" className="button button--ghost" onClick={retry}>
                                Try again
                            </button>
                        </div>
                    )}

                    {config && !sessionError && (
                        <>
                            {mastering.phase === 'idle' && (
                                <Dropzone
                                    acceptedFileTypes={config.acceptedFileTypes}
                                    maxUploadBytes={config.maxUploadBytes}
                                    onFile={(file) => void mastering.start(file)}
                                />
                            )}

                            {busy && (
                                <div className="working">
                                    <div className="working__disc" aria-hidden="true">
                                        <span className="working__label" />
                                    </div>
                                    <p className="working__status">{PHASE_LABELS[mastering.phase]}</p>
                                    <p className="working__file">{mastering.fileName}</p>
                                    <div className="working__bar">
                                        <span style={{ width: `${mastering.totalProgress}%` }} />
                                    </div>
                                    <p className="working__percent">{mastering.totalProgress}%</p>
                                </div>
                            )}

                            {mastering.phase === 'failed' && (
                                <div className="alert alert--error">
                                    <p>{mastering.error}</p>
                                    <button type="button" className="button button--ghost" onClick={mastering.reset}>
                                        Try another track
                                    </button>
                                </div>
                            )}

                            {mastering.phase === 'ready' && mastering.job && (
                                <div className="result">
                                    <ComparePlayer
                                        title={mastering.job.trackName}
                                        originalUrl={mastering.job.previewOriginalUrl ?? ''}
                                        masteredUrl={mastering.job.previewMasteredUrl ?? ''}
                                    />

                                    {mastering.job.paid && mastering.job.downloadUrl ? (
                                        <div className="download">
                                            <a
                                                className="button button--primary"
                                                href={mastering.job.downloadUrl}
                                                download={mastering.job.downloadFileName}
                                            >
                                                Download your master
                                            </a>
                                            <p className="download__note">
                                                The link is valid for a limited time — reload this page to get a
                                                fresh one.
                                            </p>
                                            <ShareBar />
                                        </div>
                                    ) : (
                                        <Checkout
                                            job={mastering.job}
                                            config={config}
                                            onPaid={mastering.markPaid}
                                        />
                                    )}

                                    <button type="button" className="button button--ghost" onClick={mastering.reset}>
                                        Master another track
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </section>
            </main>

            <section className="how">
                <h2>How it works</h2>
                <ol>
                    <li>
                        <strong>Upload</strong>
                        Drop your final mix. It goes straight to our secure storage.
                    </li>
                    <li>
                        <strong>Listen</strong>
                        Compare the original and the mastered preview, for free.
                    </li>
                    <li>
                        <strong>Download</strong>
                        Happy with it? Pay once and grab the full 24 bit master.
                    </li>
                </ol>
            </section>

            <footer className="site-footer">
                <nav>
                    <a href="#privacy-policy">Privacy Policy</a>
                    <a href="#terms-and-conditions">Terms and Conditions</a>
                    <a href="#faq">FAQ</a>
                    <a href="mailto:fivedollamastering@gmail.com">Contact Us</a>
                </nav>
                <p>© {new Date().getFullYear()} Five Dollar Mastering</p>
            </footer>

            {page && <PageModal slug={page} onClose={closePage} />}
        </div>
    );
}

function ShareBar() {
    const url = 'https://fivedollarmastering.com';
    const text = 'Check out this cool AI mastering';
    const links = [
        { label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${url}` },
        { label: 'X', href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${text} ${url}`)}` },
        { label: 'Telegram', href: `https://t.me/share/url?url=${url}&text=${encodeURIComponent(text)}` },
    ];

    return (
        <div className="share">
            <span>Like it? Share it</span>
            <div className="share__links">
                {links.map((link) => (
                    <a key={link.label} href={link.href} target="_blank" rel="noreferrer noopener">
                        {link.label}
                    </a>
                ))}
            </div>
        </div>
    );
}
